/**
 * The scenario list under one scenario type. A row leads with who wrote in, or the
 * description when the scenario names no customer, and the opening line, and a fabricated
 * one says so. Clicking it reads the scenario beside the list; its checkbox picks it for a
 * bulk run or dataset; the other actions wait in a menu that shows on hover.
 */

import { History, MoreHorizontal, Play, Ticket, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { previewText, scenarioWho } from '../format';
import { KindBadge } from '../kind-badge';
import type { Scenario } from '../types';

/** The two lines a scenario row leads with, both through `previewText` since a scraped description carries ticket markup. */
export const ScenarioRowTitle = ({
	title,
	subtitle,
	emphasis = false,
	className
}: {
	title: string;
	subtitle?: string | null;
	/** The row being read: its name steps forward. */
	emphasis?: boolean;
	className?: string;
}) => (
	<span className={cn('flex min-w-0 flex-col', className)}>
		<span className={cn('truncate text-[13px] leading-4.5 font-medium', emphasis ? 'text-text-heading' : 'text-text-body')}>
			{previewText(title)}
		</span>
		{subtitle && <span className='truncate text-xs leading-4 text-gray-800'>{previewText(subtitle)}</span>}
	</span>
);

/** The loading stand-in for a scenario list, shared by every surface that renders one. */
export const ScenarioRowsSkeleton = () => (
	<div className='flex flex-col gap-2 p-3'>
		{Array.from({ length: 3 }, (_, i) => (
			<Skeleton key={i} className='h-11' />
		))}
	</div>
);

interface ScenarioRowsProps {
	scenarios: Scenario[];
	/** The one being read beside the list. */
	activeId?: string | null;
	/** Picked for the next bulk run or dataset. */
	selectedIds?: Set<string>;
	/** Absent when the list cannot be picked from, as in the untyped group. */
	onToggleSelected?: (scenario: Scenario) => void;
	/** The selection is full; the unpicked rows say so rather than going quiet. */
	selectionFull?: boolean;
	/** Picking cases for a dataset: the scenarios it already tests, shown picked and fixed. */
	inDatasetIds?: ReadonlySet<string>;
	/** Reads the scenario beside the list: its whole thread, and the button that runs it. */
	onOpen: (scenario: Scenario) => void;
	/** Runs it in the simulator straight from the list. */
	onTest: (scenario: Scenario) => void;
	onDelete: (scenario: Scenario) => void;
	/** Opens the scenario's run history; absent in the untyped group. */
	onHistory?: (scenario: Scenario) => void;
}

export const ScenarioRows = ({
	scenarios,
	activeId,
	selectedIds,
	onToggleSelected,
	selectionFull,
	inDatasetIds,
	onOpen,
	onTest,
	onDelete,
	onHistory
}: ScenarioRowsProps) => (
	<div className='flex flex-col divide-y divide-gray-500'>
		{scenarios.map((scenario) => {
			const who = scenarioWho(scenario);
			const inDataset = inDatasetIds?.has(scenario.id) ?? false;
			const picked = inDataset || (selectedIds?.has(scenario.id) ?? false);
			const selectable = Boolean(onToggleSelected);
			const full = !picked && selectionFull;
			return (
				<div
					key={scenario.id}
					className={cn(
						'group relative flex items-center gap-2 pr-2 transition-colors hover:bg-gray-100/60',
						activeId === scenario.id && 'bg-gray-100/60'
					)}
				>
					{/* Outside the checkbox, so the bar marks the whole row rather than the text. */}
					{activeId === scenario.id && <span className='absolute inset-y-0 left-0 w-[2px] bg-foreground/70' />}

					{selectable && (
						<span className='pl-3'>
							<Checkbox
								checked={picked}
								disabled={inDataset || full}
								onCheckedChange={() => onToggleSelected?.(scenario)}
								aria-label={`Select ${who ?? scenario.description}`}
								title={inDataset ? 'Already a case in this dataset' : full ? 'The selection is full' : undefined}
							/>
						</span>
					)}

					<button
						type='button'
						onClick={() => onOpen(scenario)}
						className={cn(
							'flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-2.5 text-left',
							selectable ? 'pl-2' : 'pl-3'
						)}
					>
						<ScenarioRowTitle
							title={who ?? scenario.description}
							subtitle={scenario.query[0]?.text || scenario.description}
							className='flex-1'
						/>
						<KindBadge kind={scenario.kind} />
						{inDataset && <span className='shrink-0 text-[11px] leading-4 text-gray-800'>In this dataset</span>}
					</button>

					{/* The row is for reading and picking; everything else you can do to it waits here. */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant='ghost'
								size='icon'
								className='size-7 shrink-0 text-gray-900 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100'
								aria-label={`Actions for ${who ?? scenario.description}`}
							>
								<MoreHorizontal className='size-3.5' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem onClick={() => onTest(scenario)}>
								<Play className='size-3.5' />
								Test in simulator
							</DropdownMenuItem>
							{onHistory && (
								<DropdownMenuItem onClick={() => onHistory(scenario)}>
									<History className='size-3.5' />
									Past runs
								</DropdownMenuItem>
							)}
							<DropdownMenuItem variant='destructive' onClick={() => onDelete(scenario)}>
								<Trash2 className='size-3.5' />
								Delete scenario
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			);
		})}
	</div>
);

/** Shown where a list would be, when the type has nothing under it yet. */
export const ScenarioRowsEmpty = ({ children }: { children: React.ReactNode }) => (
	<p className='flex items-center gap-2 px-3 py-3 text-xs leading-4 text-gray-800'>
		<Ticket className='size-3.5 shrink-0' />
		{children}
	</p>
);
